import {
  type HTMLAttributes,
  type ReactNode,
  cloneElement,
  isValidElement,
} from 'react';

import classNames from 'classnames';

import styles from './Text.module.css';
import colorStyles from './colors.module.css';
import variantStyles from './variants.module.css';

import type { ColorName } from '../../styles';
import type { AlignProps } from '../../styles/align';
import { getAlignStyles } from '../../styles/align';
import { type SpacingProps, getSpacingStyles } from '../../styles/spacing';

export const tiemposHeadlineArticleVariants = [
  'article-28',
  'article-40',
  'article-52',
  'article-72',
];

export const headlineVariants = [
  'heading-72',
  'heading-52',
  'heading-40',
  'heading-28',
  'heading-22',
  'heading-20',
  'heading-16',
  'heading-12',
];

// These variants wants a black weight when bold.
export const blackVariants = [
  'heading-72',
  'heading-52',
  'heading-40',
  'heading-28',
];

export const lazyDogVariants = [
  'lazy-dog-32',
  'lazy-dog-24',
  'lazy-dog-22',
  'lazy-dog-16',
  'lazy-dog-64',
  'lazy-dog-72',
];

export type TextElement =
  | 'span'
  | 'p'
  | 'div'
  | 'blockquote'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'dt'

  // We should avoid using these elements in favor of `asChild` prop
  | 'label'
  | 'legend'
  | 'ul'
  | 'li'
  | 'td'
  | 'tr'
  | 'a'
  | 'i'
  | 'cite'
  | 'article'
  | 'figcaption';

export type TextVariant =
  | 'heading-72'
  | 'heading-52'
  | 'heading-40'
  | 'heading-28'
  | 'heading-22'
  | 'heading-20'
  | 'heading-16'
  | 'heading-13'
  | 'heading-12'
  | 'article-72'
  | 'article-52'
  | 'article-40'
  | 'article-28'
  | 'article-20'
  | 'article-16'
  | 'article-12'
  | 'lazy-dog-32'
  | 'lazy-dog-24'
  | 'lazy-dog-22'
  | 'lazy-dog-16'
  | 'lazy-dog-64'
  | 'lazy-dog-72';

/** @deprecated Use `ColorName` instead. */
export type TextColor =
  | 'blueberry-2'
  | 'blueberry-3'
  | 'carrot-2'
  | 'carrot-3'
  | 'charcoal-1'
  | 'charcoal-2'
  | 'charcoal-3'
  | 'kale-1'
  | 'kale-2'
  | 'kale-3'
  | 'white'
  | 'error-red';

/** The props for the {@link Text} component. */
export interface TextProps
  extends SpacingProps, AlignProps, HTMLAttributes<HTMLElement> {
  /** @defaultValue 'heading-16' */
  variant?: TextVariant;

  /** The HTML element to render. */
  as?: TextElement;

  /** Wrap any HTML element to apply the styles. */
  asChild?: boolean;

  fontStyle?: 'normal' | 'italic';
  bold?: boolean;
  italic?: boolean;
  color?: TextColor | ColorName;
  underline?: boolean;
  uppercase?: boolean;
  weight?: 'light' | 'normal' | 'medium' | 'semibold' | 'bold';
  children?: ReactNode;
}

/**
 * Render a styled text.
 *
 * @cssVariable --tfd-heading-body-font-family The font-family for heading variants size 16 and below.
 * @cssVariable --tfd-heading-12-letter-spacing The letter spacing for the heading-12 variant with normal weight.
 * @cssVariable --tfd-heading-12-letter-spacing-semibold The letter spacing for the heading-12 variant with semibold weight.
 * @cssVariable --tfd-heading-12-letter-spacing-bold The letter spacing for the heading-12 variant with bold weight.
 * @cssVariable --tfd-heading-16-letter-spacing The letter spacing for the heading-16 variant with normal weight.
 * @cssVariable --tfd-heading-16-letter-spacing-semibold The letter spacing for the heading-16 variant with semibold weight.
 * @cssVariable --tfd-heading-16-letter-spacing-bold The letter spacing for the heading-16 variant with bold weight.
 * @see https://corgi-x.tfd.engineering/components/text
 */
export function Text(props: TextProps) {
  const {
    as: Component = 'span' as TextElement,
    asChild,
    variant = 'heading-16',
    bold,
    fontStyle,
    color,
    weight,
    italic,
    underline,
    uppercase,
    align,
    spacing,
    topSpacing,
    bottomSpacing,
    vSpacing,
    leftSpacing,
    rightSpacing,
    hSpacing,

    children,
    ...elementProps
  } = props;

  const spacingStyles = getSpacingStyles('margin', props);
  const alignStyles = getAlignStyles(props);

  const isItalic = italic || fontStyle === 'italic';

  let adjustedWeight = bold
    ? 'bold'
    : weight === 'medium' && headlineVariants.includes(variant)
      ? 'medium'
      : '';

  if (bold && blackVariants.includes(variant)) {
    adjustedWeight = 'black';
  }

  if (bold && tiemposHeadlineArticleVariants.includes(variant) && !isItalic) {
    adjustedWeight = 'semibold';
  } else if (
    bold &&
    tiemposHeadlineArticleVariants.includes(variant) &&
    isItalic
  ) {
    adjustedWeight = '';
  }

  // If tiempos-headline is italic, the font weight should be normal.
  if (tiemposHeadlineArticleVariants.includes(variant) && isItalic) {
    adjustedWeight = '';
  }

  // If the weight is set and the variant is an article, allow only normal and semibold.
  if (
    weight &&
    tiemposHeadlineArticleVariants.includes(variant) &&
    (weight === 'normal' || weight === 'semibold')
  ) {
    adjustedWeight = weight;
  }

  // If the weight is set and the variant is a heading, use the weight.
  if (weight && variant.includes('heading')) {
    adjustedWeight = weight;
  }

  // If weight is light, only apply it to lazy-dog variants.
  if (weight === 'light' && variant.includes('lazy-dog')) {
    adjustedWeight = 'light';
  }

  const className = classNames(
    styles.Text,
    variantStyles[variant],
    fontStyle &&
      (fontStyle === 'normal'
        ? variantStyles.styleNormal
        : variantStyles.italic),
    italic && variantStyles.italic,
    underline && variantStyles.underline,
    uppercase && variantStyles.uppercase,
    color && colorStyles[color],
    adjustedWeight && variantStyles[adjustedWeight],
    alignStyles,
    spacingStyles,
    props.className
  );

  if (asChild && isValidElement(children)) {
    return cloneElement(children, {
      className: classNames([children.props.className, className]),
      ...elementProps,
    });
  }

  return (
    <Component {...elementProps} className={className}>
      {children}
    </Component>
  );
}

/** @deprecated Use `TextProps['color']`. */
export type ArticleColors = TextProps['color'];

/** @deprecated Use `TextProps['color']`. */
export type HeadingColors = TextProps['color'];

/** @deprecated Use `TextProps['variant']`. */
export type VariantColors = TextProps['color'];

/** @deprecated Use `TextVariant` (singular.) */
export type TextVariants = TextVariant;
